import { WsDialogs } from "interfaces";
import { RootState } from "redux/store";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { FeaturesFlags } from "helpers";
import { getDebugLogger } from "utils/debugLogger";
import { useEnvContext } from "contexts/EnvProvider";
import { useWebStoreContext } from "contexts/WebStoreProvider";
import { useLocation, useSearchParams } from "react-router-dom";
import {
	useAppDispatch,
	useAppSelector,
	useLinks,
	usePopupsState
} from "hooks";
import { useDialogModalRedux } from "redux/slices/dialog/hooks/useDialogModalRedux";
import useNewDay from "hooks/useNewDay";
import {
	getDIPs,
	onDIPtrigger,
	onResolveSessionStartDIP,
	selectCommonState,
	selectDipState,
	selectPopUpManagerState,
	selectPopUpManagerV2State
} from "redux/slices";

export const useInitialActions = (): void => {
	const location = useLocation();
	const dispatch = useAppDispatch();
	const { newDay } = useNewDay();
	const { isFeatureFlagEnabled } = useEnvContext();
	const { authToken, isLoggingOut } = useWebStoreContext();
	const isDIPsEnabled = isFeatureFlagEnabled(FeaturesFlags.DIPS);
	const { queueTriggers, opened, triggerCached, sessionStartDipStatus } =
		useAppSelector(selectDipState);
	const { isChecking: IsInAppToWebDipChecking } = useAppSelector(
		(state: RootState) => state.inAppToWebDip
	);
	const isMegaMenuOpen = useSelector(
		(state: RootState) => state.megaMenu.isOpen
	);
	const { variant } = useDialogModalRedux();
	const { variant: popupManagerVariant } = useAppSelector(
		selectPopUpManagerState
	);
	const { stack: popupManagerV2Stack } = useAppSelector(
		selectPopUpManagerV2State
	);
	const [searchParams] = useSearchParams();
	const {
		isSignupChecking,
		hasSeenLegacyTutorials,
		IsFriendReferralAcceptInviteChecking
	} = usePopupsState();
	const isPopupManagerV2Open = popupManagerV2Stack.length > 0;
	const { isLPAnimationPlaying } = useAppSelector(selectCommonState);
	const searchParamsInvalidate: boolean = searchParams.has("deeplinkId");
	const [suppressDips] = useState<boolean>(
		searchParams.get("focus") === "rebate"
	);

	const links = useLinks();

	const debug = getDebugLogger({
		isEnabled: true
	});

	const isFtueV2Enabled = isFeatureFlagEnabled(FeaturesFlags.FTUE_V2);
	const shouldWaitForTutorialBeforeDip =
		!isFtueV2Enabled && !hasSeenLegacyTutorials;
	const shouldWaitForSignupBeforeDip = !isFtueV2Enabled && !!isSignupChecking;

	/**
	 * Load DIPs after authentication/feature activation and whenever the
	 * Pacific calendar day changes.
	 */
	useEffect(() => {
		if (!isDIPsEnabled || !authToken?.value) {
			return;
		}
		dispatch(getDIPs());
	}, [authToken?.value, isDIPsEnabled, newDay]);

	// 🔦 dispatch sessionStart trigger when DIP data is on state
	useEffect(() => {
		if (sessionStartDipStatus !== "ready" || shouldWaitForTutorialBeforeDip) {
			return;
		}

		if (
			searchParamsInvalidate ||
			(suppressDips && location.pathname === links.shop.path)
		) {
			dispatch(onResolveSessionStartDIP());
			return;
		}

		if (
			!isMegaMenuOpen &&
			variant !== WsDialogs.LOGIN_REWARD_CONFIRMATION &&
			!popupManagerVariant &&
			!isPopupManagerV2Open &&
			!isLPAnimationPlaying &&
			!IsInAppToWebDipChecking &&
			!IsFriendReferralAcceptInviteChecking &&
			!shouldWaitForSignupBeforeDip
		) {
			debug.logUseEffect({
				iconText: "DIPs Initial Actions",
				message: ["sessionStart"]
			});
			dispatch(onDIPtrigger({ triggerType: "sessionStart" }));
			dispatch(onResolveSessionStartDIP());
		}
	}, [
		sessionStartDipStatus,
		isMegaMenuOpen,
		location.pathname,
		searchParamsInvalidate,
		isLPAnimationPlaying,
		IsInAppToWebDipChecking,
		IsFriendReferralAcceptInviteChecking,
		suppressDips,
		shouldWaitForTutorialBeforeDip,
		shouldWaitForSignupBeforeDip,
		variant,
		popupManagerVariant,
		isPopupManagerV2Open
	]);

	useEffect(() => {
		if (
			location.pathname !== links.home.path &&
			!!authToken?.value &&
			!isLoggingOut &&
			!shouldWaitForTutorialBeforeDip
		) {
			debug.logUseEffect({
				iconText: "DIPs Initial Actions",
				message: ["on path change trigger cache:", "cache-mode"]
			});
			dispatch(onDIPtrigger({ triggerType: "cache-mode" }));
		}
	}, [
		location.pathname,
		shouldWaitForTutorialBeforeDip,
		authToken?.value,
		isLoggingOut
	]);

	// When a DIP is closed we check if there is a DIP on queue to show it.
	// Queue DIPs are created when a DIP try to be displayed in the same
	// time that there's another one on the screen
	useEffect(() => {
		if (!opened && !!queueTriggers.length) {
			dispatch(onDIPtrigger({ triggerType: queueTriggers[0] }));
		}
	}, [opened, queueTriggers]);

	// When a DIP is closed we check if there is a DIP on cache.
	useEffect(() => {
		if (!opened && triggerCached) {
			debug.logUseEffect({
				iconText: "DIPs Initial Actions: ",
				message: ["on close dip check cache:", "cache-mode"]
			});
			dispatch(onDIPtrigger({ triggerType: "cache-mode" }));
		}
	}, [opened, triggerCached]);
};
